import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { Web3DViewer } from '@technology/web3d';
import {
  CADEventName,
  LineEditingAction,
} from '@technology/web3d-plugin-line-editing';
import {
  FeatureEditingController,
  PointCloudCrsAuthoring,
  SceneToGeographic,
} from '@trimble/geospatial';
import { ILogger } from '@trimble/logging';
import { batch } from 'solid-js';

export interface CreateFeatureEditingBridgeParams {
  readonly viewer: Web3DViewer;
  readonly context: FeatureExtractionContextType;
  readonly logger: ILogger;
  getSceneToGeographic(): SceneToGeographic | undefined;
  getPointCloudCrsAuthoring(): PointCloudCrsAuthoring | undefined;
}

export interface FeatureEditingBridge {
  dispose(): void;
}

export function createFeatureEditingBridge(
  params: CreateFeatureEditingBridgeParams
): FeatureEditingBridge {
  const {
    viewer,
    context,
    logger,
    getSceneToGeographic,
    getPointCloudCrsAuthoring,
  } = params;
  const controller = new FeatureEditingController({
    getFeatureService: () => context.featureRepository(),
    logger,
    getScope: () => context.scope(),
    getActiveLayer: () => context.selectedLayer(),
    getCrsUrn: () => context.pointCloudCrs(),
    getSceneToGeographic,
    getPointCloudCrsAuthoring,
    // Rotate after writes so CloudFront does not serve pre-edit tiles (shared).
    onFeaturesChanged: () => context.bumpTileCacheToken(),
    onFeaturePersisted: (feature) => {
      if (!feature.id) {
        return;
      }
      context.upsertFeature(feature);
    },
    onFeaturesRemoved: (featureIds) => {
      batch(() => {
        featureIds.forEach((id) => {
          context.removeFeature(id);
        });
        const selectedId = context.editingState.selectedFeature?.id;
        if (selectedId !== undefined && featureIds.includes(selectedId)) {
          context.setSelectedFeature(undefined);
        }
      });
    },
  });

  controller.start((handler) => {
    const actionListener = (event: Event): void => {
      const detail = (event as CustomEvent<LineEditingAction>).detail;
      if (detail) {
        handler(detail);
      }
    };
    viewer.addEventListener(CADEventName.ActionPerformed, actionListener);
    return () => {
      viewer.removeEventListener(CADEventName.ActionPerformed, actionListener);
    };
  });

  return {
    dispose: () => {
      controller.stop();
    },
  };
}
